/**
 * CUSTOMER FIX Stage 7 — registry persistence writes (no cartStorage import).
 */

export const CART_REGISTRY_STORAGE_KEY = 'rpapp-customer.shop.cart.registry' as const;
export const CART_REGISTRY_VERSION = 1 as const;
export const CUSTOMER_CART_REGISTRY_CHANGED_EVENT =
  'customer:cart-registry-changed' as const;

export interface CartRegistryEntryV1 {
  readonly tenantCode: string;
  readonly kioskId: number;
  readonly tenantName: string;
  readonly salesPointLabel: string;
  readonly shopHref?: string;
  readonly totalQuantity: number;
  readonly updatedAt: number;
}

interface PersistedCartRegistryV1 {
  readonly v: typeof CART_REGISTRY_VERSION;
  readonly entries: CartRegistryEntryV1[];
}

function cartRegistryEntryKey(tenantCode: string, kioskId: number): string {
  return `${tenantCode}:${kioskId}`;
}

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return null;
  }
  try {
    const probeKey = `${CART_REGISTRY_STORAGE_KEY}:probe`;
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    return null;
  }
}

function emitRegistryChanged(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(CUSTOMER_CART_REGISTRY_CHANGED_EVENT));
}

export function readRawCartRegistry(): PersistedCartRegistryV1 {
  const storage = safeLocalStorage();
  if (storage === null) {
    return { v: CART_REGISTRY_VERSION, entries: [] };
  }
  const raw = storage.getItem(CART_REGISTRY_STORAGE_KEY);
  if (raw === null) {
    return { v: CART_REGISTRY_VERSION, entries: [] };
  }
  try {
    const parsed = JSON.parse(raw) as PersistedCartRegistryV1;
    if (parsed.v !== CART_REGISTRY_VERSION || !Array.isArray(parsed.entries)) {
      return { v: CART_REGISTRY_VERSION, entries: [] };
    }
    return parsed;
  } catch {
    return { v: CART_REGISTRY_VERSION, entries: [] };
  }
}

/** True when persisted registry JSON is missing or structurally invalid (G6 / storm recovery). */
export function isCartRegistryRawCorrupt(raw: string | null): boolean {
  if (raw === null) {
    return false;
  }
  try {
    const parsed = JSON.parse(raw) as { v?: unknown; entries?: unknown };
    if (parsed.v !== CART_REGISTRY_VERSION || !Array.isArray(parsed.entries)) {
      return true;
    }
    return false;
  } catch {
    return true;
  }
}

export function writeCartRegistry(entries: readonly CartRegistryEntryV1[]): void {
  const storage = safeLocalStorage();
  if (storage === null) {
    return;
  }
  const payload: PersistedCartRegistryV1 = {
    v: CART_REGISTRY_VERSION,
    entries: [...entries],
  };
  const nextJson = JSON.stringify(payload);
  try {
    const priorJson = storage.getItem(CART_REGISTRY_STORAGE_KEY);
    if (priorJson === nextJson) {
      return;
    }
    storage.setItem(CART_REGISTRY_STORAGE_KEY, nextJson);
    emitRegistryChanged();
  } catch {
    // Quota — registry is UX metadata only.
  }
}

export interface UpsertCartRegistryEntryInput {
  readonly tenantCode: string;
  readonly kioskId: number;
  readonly tenantName?: string;
  readonly salesPointLabel?: string;
  readonly shopHref?: string;
  readonly totalQuantity: number;
}

export function upsertCartRegistryEntry(input: UpsertCartRegistryEntryInput): void {
  const tenantCode = input.tenantCode.trim();
  const kioskId = Math.floor(input.kioskId);
  if (tenantCode.length === 0 || kioskId <= 0) {
    return;
  }

  const key = cartRegistryEntryKey(tenantCode, kioskId);
  const existing = readRawCartRegistry();
  const prior = existing.entries.find(
    (entry) => cartRegistryEntryKey(entry.tenantCode, entry.kioskId) === key,
  );

  if (input.totalQuantity <= 0) {
    const next = existing.entries.filter(
      (entry) => cartRegistryEntryKey(entry.tenantCode, entry.kioskId) !== key,
    );
    if (next.length !== existing.entries.length) {
      writeCartRegistry(next);
    }
    return;
  }

  const row: CartRegistryEntryV1 = {
    tenantCode,
    kioskId,
    tenantName: input.tenantName?.trim() || prior?.tenantName || tenantCode,
    salesPointLabel:
      input.salesPointLabel?.trim() || prior?.salesPointLabel || String(kioskId),
    shopHref: input.shopHref?.trim() || prior?.shopHref,
    totalQuantity: input.totalQuantity,
    updatedAt: Date.now(),
  };

  const without = existing.entries.filter(
    (entry) => cartRegistryEntryKey(entry.tenantCode, entry.kioskId) !== key,
  );
  writeCartRegistry([row, ...without]);
}

export function removeCartRegistryEntry(tenantCode: string, kioskId: number): void {
  upsertCartRegistryEntry({ tenantCode, kioskId, totalQuantity: 0 });
}
